import { effect, inject, Injectable, OnDestroy, signal } from '@angular/core';
import { currentTheme, getTheme } from 'devextreme/viz/themes';
import { BehaviorSubject, Subscription } from 'rxjs';
import { SiaParamsAndPatternsService } from './params-and-patterns.service';

@Injectable({
  providedIn: 'root',
})
export class ThemeService implements OnDestroy {
  private paramsAndPatternsService = inject(SiaParamsAndPatternsService);

  themeChanged: BehaviorSubject<string> = new BehaviorSubject('');
  #baseThemeColor = signal<string>('');
  #variantThemeColor = signal<string>('');
  body = document.getElementsByTagName('body')[0];

  #themeColorSubscription?: Subscription;

  #applyThemeEffect = effect(() => {
    const baseThemeColor = this.#baseThemeColor();
    const variantThemeColor = this.#variantThemeColor();
    this.applyTheme();
    this.themeChanged.next(`${baseThemeColor}-${variantThemeColor}`);
  });

  #persistThemeEffect = effect(() => {
    localStorage.setItem('sia-theme-base', this.#baseThemeColor());
    localStorage.setItem('sia-theme-variant', this.#variantThemeColor());
  });

  constructor() {
    this.#themeColorSubscription =
      this.paramsAndPatternsService.themeColorChanged$.subscribe(
        (color: string) => {
          if (color) this.#baseThemeColor.set(color);
        },
      );
    this.#variantThemeColor.set(
      localStorage.getItem('sia-theme-variant') || 'light',
    );
    this.#baseThemeColor.set(
      localStorage.getItem('sia-theme-base') || 'default',
    );
  }

  getThemeItem(...keys: Array<string>): any {
    const theme = getTheme(currentTheme());
    let item = theme;
    for (let key in keys) item = item[keys[key]];
    return item;
  }

  setThemeVariant(theme: string) {
    this.#variantThemeColor.set(theme);
  }

  setThemeColor(color: string) {
    this.#baseThemeColor.set(color);
  }

  applyTheme() {
    this.body.className = '';

    if (this.#variantThemeColor() === 'dark') {
      this.body.className += `dark`;
    } else {
      this.body.className += `light`;
    }
    this.body.className += ` ${this.#baseThemeColor()}-theme`;
  }

  getCurrentTheme(): string {
    return window.localStorage['sia-theme-variant'] ?? 'light';
  }

  ngOnDestroy(): void {
    this.#themeColorSubscription?.unsubscribe();
  }
}
